import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { EnvironmentService } from '../@core/services/environment.service';
import { S3Service } from './s3.service';

export enum MediaType {
  FILE = 'FILE',
  FOLDER = 'FOLDER',
}

export interface S3PresignedUrl {
  createdAt: Date;
  id: string;
  name: string;
  updatedAt: Date;
  url: string;
}

// TODO: refactor to import these interfaces from the BE
export interface RepositoryMediaFile {
  id: string;
  name: string;
  slug?: string;
  downloadable: boolean;
  audiences: string[];
  url: string;
  type: MediaType
  createdAt: Date;
  updatedAt: Date;
}

export interface MediaUploadProgress {
  fileId: string;
  fileName: string;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
}

export interface PaginatedMediaResponse {
  items: RepositoryMediaFile[];
  totalCount: number;
  paginationInfo: {
    page: number;
    size: number;
    count: number;
    hasNext: boolean;
  };
}

export interface MediaSearchQuery {
  page?: number;
  size?: number;
  search?: string;
  folderId?: string;
  type?: MediaType;
  getTotalCount?: boolean;
  orderBy?: string;
  sortBy?: string
}

@Injectable({
  providedIn: 'root'
})
export class RepositoryService {
  private readonly baseUrl: string;
  private uploadProgressSubject = new BehaviorSubject<MediaUploadProgress[]>([]);

  public uploadProgress$ = this.uploadProgressSubject.asObservable();

  constructor(
    private http: HttpClient,
    private environmentService: EnvironmentService,
    private s3Service: S3Service
  ) {
    // Using utilities service for media management
    this.baseUrl = `${this.environmentService.utilitiesServiceUrl}/repository`;
  }

  /**
   * Get paginated media files
   */
  getMediaFiles(query: MediaSearchQuery = {}): Observable<PaginatedMediaResponse> {
    let params = new HttpParams();

    // TODO: set correct params based on BE implementation
    if (query.page) params = params.set('page', query.page.toString());
    if (query.size) params = params.set('size', query.size.toString());
    if (query.search) params = params.set('search', query.search);
    if (query.folderId) params = params.set('parent', query.folderId);
    if (query.type) params = params.set('type', query.type);
    if (query.getTotalCount !== undefined) params = params.set('getTotalCount', query.getTotalCount.toString());
    if (query.orderBy) params = params.set('orderBy', query.orderBy);
    if (query.sortBy) params = params.set('sortBy', query.sortBy);

    // TODO: set correct URL based on BE implementation
    return this.http.get<PaginatedMediaResponse>(`${this.baseUrl}`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get paginated media files
   */
  getMediaById(id: string | undefined): Observable<RepositoryMediaFile> {
    return this.http.get<RepositoryMediaFile>(`${this.baseUrl}/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  validateTokenAndGetMediaById(id: string | undefined, token: string | undefined): Promise<{ media: RepositoryMediaFile | null, valid: boolean }> {
    return new Promise((resolve) => {
      fetch(`${this.baseUrl}/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': 'it',
          'Authorization': `Bearer ${token}`,
        }
      })
        .then(response => {
          if (response.ok) {
            resolve({ media: response.json() as unknown as RepositoryMediaFile, valid: true });
          } else {
            resolve({ media: null, valid: false });
          }
        })
        .catch(() => {
          resolve({ media: null, valid: false });
        });
      // this.http.get<boolean>(this.apiAppUrl + '/validate-token', { params: { token } }).subscribe({
      //   next: (res) => {
      //     resolve(res);
      //   },
      //   error: (err) => {
      //     resolve(false);
      //   },
      // });

    });
  }

  /**
   * Create a new folder
   */
  createFolder(name: string, downloadable: boolean, audiences: string[], parent?: string): Observable<RepositoryMediaFile> {
    const body = { name, parent, type: MediaType.FOLDER, downloadable, audiences };
    return this.http.post<RepositoryMediaFile>(`${this.baseUrl}`, body)
      .pipe(
        catchError(this.handleError)
      );
  }

  getPresigned(name: string, downloadable: boolean, audiences: string[], parent?: string,): Observable<S3PresignedUrl> {
    const body = { name, parent, type: MediaType.FILE, downloadable, audiences };
    return this.http.post<S3PresignedUrl>(`${this.baseUrl}`, body).pipe(
      catchError(this.handleError)
    );
  }

  async downloadFileAsync(file: RepositoryMediaFile): Promise<Response> {
    return fetch(file.url, {
      method: 'GET',
      headers: {
        'Access-Control-Allow-Origin': '*'
      }
    })
  }

  async putPresignedAsync(file: File, presignedUrl: string): Promise<void> {
    await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });
  }

  /**
   * Upload multiple files with progress tracking using S3 presigned URLs
   */
  uploadFiles(files: File[], parent?: string): Observable<RepositoryMediaFile[]> {
    // Initialize progress tracking for all files
    files.forEach(file => {
      this.updateUploadProgress(file.name, 0, 'uploading');
    });

    // Generate upload URLs for all files
    const uploadUrlRequests = files.map(file => {
      const fileKey = `${Date.now()}_${file.name}`;
      return this.s3Service.generateUploadUrl(parent || '', fileKey, `${this.baseUrl}`).pipe(
        map(response => ({ file, uploadUrl: response.url, fileKey: response.key })),
        catchError(error => {
          this.updateUploadProgress(file.name, 0, 'error', `Failed to generate upload URL: ${error.message}`);
          return throwError(() => error);
        })
      );
    });

    return forkJoin(uploadUrlRequests).pipe(
      switchMap(uploadData => {
        // Upload all files to their respective presigned URLs
        const uploadRequests = uploadData.map(({ file, uploadUrl, fileKey }) => {
          return this.s3Service.uploadFile(uploadUrl, file, fileKey).pipe(
            tap(() => {
              this.updateUploadProgress(file.name, 100, 'completed');
            }),
            map(() => ({
              // Create a temporary MediaFile object
              // In a real implementation, you might need to call another endpoint
              // to register the uploaded file in your database
              id: fileKey,
              name: fileKey,
              originalName: file.name,
              type: MediaType.FILE,
              mimeType: file.type,
              size: file.size,
              url: uploadUrl, // This would typically be the permanent URL
              parent,
              downloadable: false,
              audiences: [],
              createdAt: new Date(),
              updatedAt: new Date()
            } as RepositoryMediaFile)),
            catchError(error => {
              this.updateUploadProgress(file.name, 0, 'error', `Upload failed: ${error.message}`);
              return throwError(() => error);
            })
          );
        });

        return forkJoin(uploadRequests);
      }),
      catchError(error => {
        // Update progress for any remaining files
        files.forEach(file => {
          const currentProgress = this.uploadProgressSubject.value;
          const existingProgress = currentProgress.find(p => p.fileName === file.name);
          if (!existingProgress || existingProgress.status === 'uploading') {
            this.updateUploadProgress(file.name, 0, 'error', error.message);
          }
        });
        return this.handleError(error);
      })
    );
  }

  /**
   * Delete a media file
   */
  deleteFile(fileId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${fileId}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Delete multiple files
   */
  deleteFiles(fileIds: string[]): Observable<void> {
    // Delete files one by one with individual requests
    if (fileIds.length === 0) {
      return new Observable(observer => {
        observer.next();
        observer.complete();
      });
    }

    // Use forkJoin to make parallel DELETE requests for each file
    return forkJoin(
      fileIds.map(fileId => this.deleteFile(fileId))
    ).pipe(
      map(() => void 0),
      catchError(this.handleError)
    );
  }


  /**
   * Validate file before upload
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    const maxSizeImage = 2 * 1024 * 1024; // 2MB for images
    const maxSizeVideo = 50 * 1024 * 1024; // 50MB for videos

    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const allowedVideoTypes = ['video/mp4'];
    const allowedAudioTypes = ['audio/mpeg'];
    const allowedDocsTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const isImage = allowedImageTypes.includes(file.type);
    const isVideo = allowedVideoTypes.includes(file.type);
    const isAudio = allowedAudioTypes.includes(file.type)
    const isDoc = allowedDocsTypes.includes(file.type);

    if (!isImage && !isVideo && !isAudio && !isDoc) {
      return {
        valid: false,
        error: 'File type not supported. Supported types: .jpg, .jpeg, .png, .mp4, .mp3, .ppt, .pptx, .xls, .xlsx, .doc, .docx, .pdf'
      };
    }

    if (isImage && file.size > maxSizeImage) {
      return {
        valid: false,
        error: 'Image file size must be less than 2MB.'
      };
    }

    if (isVideo && file.size > maxSizeVideo) {
      return {
        valid: false,
        error: 'Video file size must be less than 50MB.'
      };
    }

    return { valid: true };
  }

  /**
   * Generate thumbnail for image file
   */
  generateThumbnail(file: File): Promise<string> {
    // TODO: Implement thumbnail generation logic
    // or call a backend service to generate it
    return Promise.resolve('');
  }

  /**
   * Clear upload progress
   */
  clearUploadProgress(): void {
    this.uploadProgressSubject.next([]);
  }

  /**
   * Update upload progress for a specific file
   */
  private updateUploadProgress(
    fileName: string,
    progress: number,
    status: 'uploading' | 'completed' | 'error',
    error?: string
  ): void {
    const currentProgress = this.uploadProgressSubject.value;
    const fileId = `${fileName}_${Date.now()}`;

    const existingIndex = currentProgress.findIndex(p => p.fileName === fileName);
    const progressItem: MediaUploadProgress = {
      fileId,
      fileName,
      progress,
      status,
      error
    };

    if (existingIndex >= 0) {
      currentProgress[existingIndex] = progressItem;
    } else {
      currentProgress.push(progressItem);
    }

    this.uploadProgressSubject.next([...currentProgress]);
  }

  editFile(id: string, name: string, downloadable: boolean, audiences: string[]) {
    const body = { name, downloadable, audiences }
    return this.http.put(`${this.baseUrl}/${id}`, body)
      .pipe(
        catchError(this.handleError)
      );

  }

  /**
   * Delete a folder and all its contents (files and subfolders)
   */
  deleteFolder(folderId: string): Observable<void> {
    return this.getMediaFiles({ folderId: folderId })
      .pipe(
        switchMap(response => {
          // First delete all files in the folder
          if (response.items.length > 0) {
            const fileIds = response.items
              .filter(item => item.type === MediaType.FILE)
              .map(file => file.id);

            if (fileIds.length > 0) {
              return this.deleteFiles(fileIds).pipe(
                // Then delete the folder itself
                switchMap(() => this.http.delete<void>(`${this.baseUrl}/${folderId}`))
              );
            }
          }
          // Check for nested folders and recursively delete them
          const nestedFolders = response.items
            .filter(item => item.type === MediaType.FOLDER)
            .map(folder => folder.id);

          if (nestedFolders.length > 0) {
            // Recursively delete each nested folder
            return forkJoin(
              nestedFolders.map(nestedId => this.deleteFolder(nestedId))
            ).pipe(
              switchMap(() => this.http.delete<void>(`${this.baseUrl}/${folderId}`))
            );
          }
          // If no files, just delete the folder
          return this.http.delete<void>(`${this.baseUrl}/${folderId}`);
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: any): Observable<never> {
    console.error('RepositoryService error:', error);

    let errorMessage = 'An unexpected error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else if (error.status) {
      // Server-side error
      switch (error.status) {
        case 400:
          errorMessage = 'Bad request. Please check your input.';
          break;
        case 401:
          errorMessage = 'Unauthorized. Please log in again.';
          break;
        case 403:
          errorMessage = 'Forbidden. You do not have permission to perform this action.';
          break;
        case 404:
          errorMessage = 'Resource not found.';
          break;
        case 413:
          errorMessage = 'File too large. Please upload a smaller file.';
          break;
        case 500:
          errorMessage = 'Internal server error. Please try again later.';
          break;
        default:
          errorMessage = `Error ${error.status}: ${error.message || 'Unknown error'}`;
      }
    }

    return throwError(() => new Error(errorMessage));
  }
}
